import { Component, type ReactNode } from "react"
import { Alert, AlertActions, AlertDescription, AlertTitle } from "@florisoft/ui"

export interface ErrorBoundaryFallbackProps {
  readonly error: Error
  /** Renders the children again. */
  retry(): void
}

interface ErrorBoundaryProps {
  readonly fallback: (props: ErrorBoundaryFallbackProps) => ReactNode
  readonly children: ReactNode
}

interface ErrorBoundaryState {
  readonly error: Error | null
}

/** Contains a render failure to its subtree and offers a retry. */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { error: error instanceof Error ? error : new Error(String(error)) }
  }

  private readonly retry = () => this.setState({ error: null })

  render() {
    const { error } = this.state
    return error === null ? this.props.children : this.props.fallback({ error, retry: this.retry })
  }
}

/** The failure message with the actions of the recovery. */
export function FailureAlert({
  title,
  error,
  children,
}: {
  readonly title: string
  readonly error: Error
  readonly children?: ReactNode
}) {
  return (
    <Alert variant="error" className="m-4 w-auto">
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription className="select-text">{error.message}</AlertDescription>
      {children === undefined ? null : <AlertActions>{children}</AlertActions>}
    </Alert>
  )
}
